/**
 * GroupNameDialog Component - Task Group 5
 *
 * Modal dialog for naming a new tool group.
 * Uses Radix Dialog following the ConfirmDialog pattern.
 *
 * Features:
 * - Input field for group name (required, 1-50 characters)
 * - Validates uniqueness within scene
 * - "Create" and "Cancel" buttons
 * - Focus trap and keyboard handling
 * - Enter to submit, Escape to cancel
 * - Inline error message display
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FolderPlus } from 'lucide-react';
import { FocusTrap } from '../common/FocusTrap';
import { useTimelineStore } from '../../stores/timelineStore';

/**
 * Group name constraints
 */
const GROUP_NAME_MIN_LENGTH = 1;
const GROUP_NAME_MAX_LENGTH = 50;

interface GroupNameDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Callback when dialog should close
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Callback when user submits a valid group name
   */
  onSubmit: (name: string) => void;
}

export function GroupNameDialog({
  open,
  onOpenChange,
  onSubmit,
}: GroupNameDialogProps) {
  // Form state
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Input ref for focus management
  const inputRef = useRef<HTMLInputElement>(null);

  // Get existing groups for uniqueness validation
  const groups = useTimelineStore((state) => state.groups);

  /**
   * Focus input when dialog opens
   * Note: State reset is not needed since the component is lazy-loaded
   * and remounts fresh each time (parent conditionally renders it)
   */
  useEffect(() => {
    if (open) {
      // Focus input after a brief delay to allow animation
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  /**
   * Validate the group name
   * Returns error message or null if valid
   */
  const validateName = useCallback(
    (value: string): string | null => {
      const trimmedValue = value.trim();

      // Check minimum length
      if (trimmedValue.length < GROUP_NAME_MIN_LENGTH) {
        return 'Group name is required';
      }

      // Check maximum length
      if (trimmedValue.length > GROUP_NAME_MAX_LENGTH) {
        return `Group name must be 50 characters or less`;
      }

      // Check uniqueness
      const nameExists = groups.some(
        (g) => g.name.toLowerCase() === trimmedValue.toLowerCase()
      );
      if (nameExists) {
        return 'A group with this name already exists';
      }

      return null;
    },
    [groups]
  );

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault();
      }

      const validationError = validateName(name);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Submit the trimmed name
      onSubmit(name.trim());
    },
    [name, validateName, onSubmit]
  );

  /**
   * Handle Cancel button click
   */
  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  /**
   * Handle input change
   * Clears error when user starts typing
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setName(e.target.value);
      if (error) {
        setError(null);
      }
    },
    [error]
  );

  /**
   * Handle key down for Enter/Escape shortcuts
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
      // Escape is handled by Radix Dialog automatically
    },
    [handleSubmit]
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            {/* Backdrop with fade animation */}
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 bg-black/70"
                style={{ zIndex: 'var(--z-index-modal-backdrop)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </Dialog.Overlay>

            {/* Dialog Content with scale + fade animation */}
            <Dialog.Content asChild>
              <motion.div
                className="fixed left-1/2 top-1/2 w-full max-w-md rounded-lg border p-6"
                style={{
                  backgroundColor: 'var(--color-dark-panel)',
                  borderColor: 'var(--color-dark-border)',
                  boxShadow: 'var(--shadow-lg)',
                  zIndex: 'var(--z-index-modal)',
                  transform: 'translate(-50%, -50%)',
                }}
                initial={{ opacity: 0, scale: 0.95, y: '-50%', x: '-50%' }}
                animate={{ opacity: 1, scale: 1, y: '-50%', x: '-50%' }}
                exit={{ opacity: 0, scale: 0.95, y: '-50%', x: '-50%' }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <FocusTrap active={open}>
                  {/* Close Button */}
                  <Dialog.Close asChild>
                    <button
                      className="absolute right-4 top-4 rounded-md p-1 text-neutral-400 transition-colors hover:bg-neutral-700 hover:text-neutral-200"
                      aria-label="Close"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </Dialog.Close>

                  {/* Title with icon */}
                  <Dialog.Title
                    className="mb-2 flex items-center gap-2 text-lg font-semibold"
                    style={{ color: 'var(--color-dark-text)' }}
                  >
                    <FolderPlus className="h-5 w-5" />
                    Create Group
                  </Dialog.Title>

                  {/* Description */}
                  <Dialog.Description
                    className="mb-4 text-sm"
                    style={{ color: 'var(--color-dark-text-muted)' }}
                  >
                    Enter a name for your new tool group. Groups can be reused
                    by dragging them from the sidebar to the timeline.
                  </Dialog.Description>

                  {/* Form */}
                  <form onSubmit={handleSubmit}>
                    {/* Name Input */}
                    <div className="mb-4">
                      <label
                        htmlFor="group-name-input"
                        className="mb-1.5 block text-sm font-medium"
                        style={{ color: 'var(--color-dark-text)' }}
                      >
                        Group Name
                      </label>
                      <input
                        ref={inputRef}
                        id="group-name-input"
                        type="text"
                        value={name}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="e.g., Wave Animation"
                        maxLength={GROUP_NAME_MAX_LENGTH + 10} // Allow typing slightly over to show error
                        className="w-full rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2"
                        style={{
                          backgroundColor: 'var(--color-dark-bg)',
                          borderColor: error
                            ? 'var(--color-body-control)'
                            : 'var(--color-dark-border)',
                          color: 'var(--color-dark-text)',
                        }}
                        aria-invalid={error ? 'true' : 'false'}
                        aria-describedby={
                          error ? 'group-name-error' : undefined
                        }
                      />

                      {/* Character count */}
                      <div
                        className="mt-1 flex justify-between text-xs"
                        style={{ color: 'var(--color-dark-text-muted)' }}
                      >
                        <span>
                          {name.length}/{GROUP_NAME_MAX_LENGTH} characters
                        </span>
                      </div>

                      {/* Error message */}
                      {error && (
                        <p
                          id="group-name-error"
                          className="mt-1 text-sm"
                          style={{ color: 'var(--color-body-control)' }}
                          role="alert"
                        >
                          {error}
                        </p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3">
                      <motion.button
                        type="button"
                        onClick={handleCancel}
                        className="rounded px-4 py-2 text-sm font-medium transition-colors"
                        style={{
                          backgroundColor: 'transparent',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: 'var(--color-dark-border)',
                          color: 'var(--color-dark-text)',
                        }}
                        whileHover={{
                          scale: 1.02,
                          backgroundColor: 'var(--color-dark-bg)',
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Cancel
                      </motion.button>

                      <motion.button
                        type="submit"
                        className="rounded px-4 py-2 text-sm font-medium text-white transition-colors"
                        style={{
                          backgroundColor: 'var(--color-body-head)',
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Create
                      </motion.button>
                    </div>
                  </form>
                </FocusTrap>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
